import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Card,
  CardContent,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Snackbar,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import {
  Home,
  Groups,
  QrCodeScanner,
  Assessment,
  Menu as MenuIcon,
  CheckCircle,
  ErrorOutline,
  Person,
  Login,
  EventNote,
  Schedule,
  LocationOn
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Html5QrcodeScanner } from 'html5-qrcode';
import api from '../utils/api';
import DesktopNav from '../components/DesktopNav';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Scanner Page - Display Student Info & Status
 * With full navigation support
 */
function ScannerPage() {
  const navigate = useNavigate();
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    // Initialize scanner
    const scanner = new Html5QrcodeScanner(
      'qr-reader',
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        rememberLastUsedCamera: true
      },
      false
    );

    scanner.render(onScanSuccess, onScanError);

    return () => {
      scanner.clear().catch(err => console.log('Scanner already cleared'));
    };
  }, []);

  const onScanSuccess = async (decodedText) => {
    setError('');
    setScanning(true);

    try {
      // Parse QR code data
      const qrData = JSON.parse(decodedText);

      // Validate QR code type
      if (qrData.type !== 'student') {
        throw new Error('Invalid QR code type. Expected student QR code.');
      }

      // Fetch student information (not check-in/out) using librarian endpoint
      const response = await api.get(`/librarian/students/${qrData.id}`);
      const studentData = response.data.data;

      // Check if student has an active session
      let activeSession = null;
      try {
        const sessionResponse = await api.get('/librarian/sessions', {
          params: {
            student_id: qrData.id,
            active: true
          }
        });
        const sessions = sessionResponse.data.data.sessions || [];
        activeSession = sessions.length > 0 ? sessions[0] : null;
      } catch (err) {
        console.log('No active session found');
      }

      // Get active booking/plan
      let activeBooking = null;
      try {
        const bookingResponse = await api.get('/bookings', {
          params: {
            student_id: qrData.id,
            status: 'active'
          }
        });
        const bookings = bookingResponse.data.data.bookings || [];
        activeBooking = bookings.length > 0 ? bookings[0] : null;
      } catch (err) {
        console.log('No active booking found');
      }

      setScanResult({
        success: true,
        student: studentData,
        activeSession,
        activeBooking,
        isCheckedIn: !!activeSession
      });

      setSnackbar({
        open: true,
        message: 'Student information loaded!',
        severity: 'success'
      });
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch student information';
      setError(errorMsg);
      setScanResult({
        success: false,
        message: errorMsg
      });

      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    } finally {
      setScanning(false);
    }
  };

  const onScanError = (errorMessage) => {
    // Ignore frequent scan errors
  };

  const handleReset = () => {
    setScanResult(null);
    setError('');
    // Reload page to restart scanner
    window.location.reload();
  };

  const handleNavChange = (event, newValue) => {
    const routes = ['/librarian', '/librarian/sessions', '/librarian/scanner', '/librarian/analytics', '/librarian/manage'];
    navigate(routes[newValue]);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Top Navigation */}
      <DesktopNav />

      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          pb: '70px',
          background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
        }}
      >
        <Container maxWidth="sm" sx={{ py: 2 }}>
          <MotionBox
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Header */}
            <MotionBox variants={itemVariants} mb={3}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="800" gutterBottom>
                  Student Scanner
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Scan to view student details and status
                </Typography>
              </Box>
            </MotionBox>

            {/* Scanner Card */}
            {!scanResult && (
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: 4,
                  mb: 2,
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    id="qr-reader"
                    sx={{
                      '& video': {
                        borderRadius: 3,
                        width: '100% !important',
                        height: 'auto !important'
                      },
                      '& #qr-shaded-region': {
                        border: '2px solid #F26522 !important',
                        borderRadius: 3
                      }
                    }}
                  />
                  {scanning && (
                    <Typography color="text.secondary" align="center" mt={2}>
                      Loading student information...
                    </Typography>
                  )}
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                </CardContent>
              </MotionCard>
            )}

            {/* Student Info Card */}
            {scanResult && scanResult.success && (
              <MotionCard
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                sx={{
                  borderRadius: 4,
                  mb: 2
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Header with Status */}
                  <Box
                    sx={{
                      p: 3,
                      background: scanResult.isCheckedIn
                        ? 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)'
                        : 'linear-gradient(135deg, #F7B32B 0%, #C88A00 100%)',
                      color: 'white',
                      textAlign: 'center'
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        margin: '0 auto',
                        mb: 2,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)',
                        fontSize: '2rem'
                      }}
                    >
                      {scanResult.student.name?.charAt(0).toUpperCase() || <Person />}
                    </Avatar>

                    <Typography variant="h5" fontWeight="800" gutterBottom>
                      {scanResult.student.name}
                    </Typography>

                    <Chip
                      icon={scanResult.isCheckedIn ? <Login /> : <ErrorOutline />}
                      label={scanResult.isCheckedIn ? 'CHECKED IN' : 'CHECKED OUT'}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        px: 2,
                        py: 0.5
                      }}
                    />
                  </Box>

                  <Divider />

                  {/* Student Details */}
                  <Box p={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Student Information
                    </Typography>

                    <Grid container spacing={2} mb={3}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {scanResult.student.email || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {scanResult.student.phone || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Age
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {scanResult.student.age || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          City
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {scanResult.student.city || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Active Session Info */}
                    {scanResult.isCheckedIn && scanResult.activeSession && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Current Session
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: 'action.hover',
                            borderRadius: 2,
                            p: 2,
                            mb: 2
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Schedule fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="600">
                              Entered at:{' '}
                              {new Date(scanResult.activeSession.entered_at).toLocaleString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                                day: 'numeric',
                                month: 'short'
                              })}
                            </Typography>
                          </Box>
                          {scanResult.activeSession.library && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <LocationOn fontSize="small" color="primary" />
                              <Typography variant="body2" color="text.secondary">
                                {scanResult.activeSession.library.name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {/* Active Booking/Plan */}
                    {scanResult.activeBooking && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Active Plan
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: 'action.hover',
                            borderRadius: 2,
                            p: 2
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <EventNote fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="600">
                              {scanResult.activeBooking.plan?.name || 'N/A'}
                            </Typography>
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Start Date
                              </Typography>
                              <Typography variant="body2">
                                {new Date(scanResult.activeBooking.start_date).toLocaleDateString('en-IN')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                End Date
                              </Typography>
                              <Typography variant="body2">
                                {new Date(scanResult.activeBooking.end_date).toLocaleDateString('en-IN')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Status
                              </Typography>
                              <Typography variant="body2">
                                <Chip
                                  label={scanResult.activeBooking.status}
                                  size="small"
                                  color="success"
                                />
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </>
                    )}

                    {!scanResult.activeBooking && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          No active plan found for this student
                        </Alert>
                      </>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleReset}
                      sx={{
                        mt: 3,
                        background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                        borderRadius: 2,
                        py: 1.5,
                        fontWeight: 700,
                        textTransform: 'none'
                      }}
                    >
                      Scan Another Student
                    </Button>
                  </Box>
                </CardContent>
              </MotionCard>
            )}

            {/* Error Card */}
            {scanResult && !scanResult.success && (
              <MotionCard
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                sx={{
                  borderRadius: 4,
                  mb: 2,
                  background: 'linear-gradient(135deg, #F7B32B 0%, #C88A00 100%)',
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      margin: '0 auto',
                      mb: 2,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <ErrorOutline sx={{ fontSize: 50 }} />
                  </Avatar>

                  <Typography variant="h5" fontWeight="800" gutterBottom>
                    Scan Failed
                  </Typography>

                  <Box
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      p: 2,
                      my: 3
                    }}
                  >
                    <Typography variant="body2">
                      {scanResult.message}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={handleReset}
                    sx={{
                      bgcolor: 'white',
                      color: '#F26522',
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)'
                      }
                    }}
                  >
                    Try Again
                  </Button>
                </CardContent>
              </MotionCard>
            )}

            {/* Instructions */}
            <MotionCard variants={itemVariants} sx={{ borderRadius: 4 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                  How It Works
                </Typography>
                <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Position student's QR code in the camera frame
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      View student information and current status
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Check active plans and session details
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Scan does NOT perform check-in/check-out
                    </Typography>
                  </li>
                </Box>
              </CardContent>
            </MotionCard>
          </MotionBox>
        </Container>
      </Box>

      {/* Bottom Navigation */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
        }}
      >
        <BottomNavigation
          value={2}
          onChange={handleNavChange}
          showLabels
          sx={{
            background: 'transparent',
            height: 70,
            '& .MuiBottomNavigationAction-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              minWidth: 'auto',
              padding: '6px 12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.9)',
              },
              '&.Mui-selected': {
                color: 'white',
                fontSize: '0.75rem',
                paddingTop: '6px',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  fontWeight: 700,
                },
                '& .MuiSvgIcon-root': {
                  transform: 'scale(1.2)',
                  filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))',
                },
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              fontWeight: 600,
              marginTop: '4px',
            },
          }}
        >
          <BottomNavigationAction label="Home" icon={<Home />} />
          <BottomNavigationAction label="Sessions" icon={<Groups />} />
          <BottomNavigationAction label="Scanner" icon={<QrCodeScanner />} />
          <BottomNavigationAction label="Analytics" icon={<Assessment />} />
          <BottomNavigationAction label="Manage" icon={<MenuIcon />} />
        </BottomNavigation>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ScannerPage;
